import { useCallback, useEffect, useState } from "react";

import type {
    AttributionScope,
    AttributionSummary,
    AttributionTrend,
    TrendGranularity,
    TrendMetricParam
} from "./attributionReportApi";
import { fetchAttributionSummary, fetchAttributionTrend } from "./attributionReportApi";

export interface AttributionQuery {
    startDate?: string;
    endDate?: string;
    attributionScope: AttributionScope;
    metric: TrendMetricParam;
    granularity: TrendGranularity;
}

export interface AttributionReportState {
    summary: AttributionSummary | null;
    trend: AttributionTrend | null;
    summaryLoading: boolean;
    trendLoading: boolean;
    /** Only ever set from a 400 — every degraded state is a normal 200 body. */
    error: string | null;
    refetch: () => void;
}

/**
 * Owns both report fetches and the rule about which control refetches what.
 *
 * The two calls are separate on purpose:
 *  - date range and attribution scope change both, with identical dates, or the
 *    cards and the chart end up describing different windows
 *  - metric and granularity change only the trend; the cards are metric-independent
 *
 * `enabled` is false in demo mode, where the tab runs entirely on fixtures and
 * must never touch the network.
 */
export function useAttributionReport(query: AttributionQuery, enabled: boolean): AttributionReportState {
    const { startDate, endDate, attributionScope, metric, granularity } = query;

    const [summary, setSummary] = useState<AttributionSummary | null>(null);
    const [trend, setTrend] = useState<AttributionTrend | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [trendLoading, setTrendLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    const refetch = useCallback(() => setNonce((n) => n + 1), []);

    // Cards: window and scope only.
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setSummaryLoading(true);
        fetchAttributionSummary({ startDate, endDate, attributionScope }).then((res) => {
            if (cancelled) return;
            setSummaryLoading(false);
            if (res.ok) {
                setSummary(res.data);
                return;
            }
            // A 400 is a malformed request — a bug, not an empty state.
            if (res.badRequest) setError(res.message);
        });
        return () => {
            cancelled = true;
        };
    }, [enabled, startDate, endDate, attributionScope, nonce]);

    // Chart + sources bar: also moves with metric and granularity.
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setTrendLoading(true);
        fetchAttributionTrend({ startDate, endDate, attributionScope, metric, granularity }).then((res) => {
            if (cancelled) return;
            setTrendLoading(false);
            if (res.ok) {
                setTrend(res.data);
                return;
            }
            if (res.badRequest) setError(res.message);
        });
        return () => {
            cancelled = true;
        };
    }, [enabled, startDate, endDate, attributionScope, metric, granularity, nonce]);

    return { summary, trend, summaryLoading, trendLoading, error, refetch };
}
